import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getTranslationFile,
  getTranslationLanguages,
  updateTranslationFile,
  TranslationSummary,
} from '../services/adminApi';

const flattenTranslationData = (data: unknown, parentKey = ''): Record<string, string> => {
  if (data === null || data === undefined) {
    return { [parentKey]: String(data) };
  }

  if (typeof data !== 'object' || Array.isArray(data)) {
    return { [parentKey]: String(data) };
  }

  return Object.entries(data as Record<string, unknown>).reduce((acc, [key, value]) => {
    const path = parentKey ? `${parentKey}.${key}` : key;
    return { ...acc, ...flattenTranslationData(value, path) };
  }, {} as Record<string, string>);
};

const unflattenTranslationData = (flatData: Record<string, string>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  const parseValue = (value: string) => {
    const trimmed = value.trim();
    if (trimmed === 'null') return null;
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
    if (trimmed !== '' && !Number.isNaN(Number(trimmed)) && String(Number(trimmed)) === trimmed) {
      return Number(trimmed);
    }
    return value;
  };

  Object.entries(flatData).forEach(([key, value]) => {
    const segments = key.split('.');
    let current: Record<string, unknown> = result;

    segments.forEach((segment, index) => {
      const isLast = index === segments.length - 1;

      if (isLast) {
        current[segment] = parseValue(value);
        return;
      }

      if (typeof current[segment] !== 'object' || current[segment] === null || Array.isArray(current[segment])) {
        current[segment] = {};
      }

      current = current[segment] as Record<string, unknown>;
    });
  });

  return result;
};

const TranslationsPage = () => {
  const { t } = useTranslation();
  const [languages, setLanguages] = useState<TranslationSummary[]>([]);
  const [activeLanguage, setActiveLanguage] = useState('');
  const [languageInput, setLanguageInput] = useState('');
  const [translationEntries, setTranslationEntries] = useState<Record<string, string>>({});
  const [newTranslationKey, setNewTranslationKey] = useState('');
  const [newTranslationValue, setNewTranslationValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadLanguages = async () => {
    setError(null);
    setMessage(null);
    setLoading(true);

    try {
      const result = await getTranslationLanguages();
      setLanguages(result);
      if (!activeLanguage && result.length > 0) {
        setActiveLanguage(result[0].languageCode);
        setLanguageInput(result[0].languageCode);
      }
    } catch (err) {
      setError(t('translations.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const loadTranslation = async (languageCode: string) => {
    if (!languageCode) {
      setTranslationEntries({});
      return;
    }

    setError(null);
    setMessage(null);
    setLoading(true);

    try {
      const file = await getTranslationFile(languageCode);
      setTranslationEntries(flattenTranslationData(file));
      setActiveLanguage(languageCode);
      setLanguageInput(languageCode);
    } catch (err) {
      setTranslationEntries({});
      setError(t('translations.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadLanguages();
  }, []);

  useEffect(() => {
    if (activeLanguage) {
      void loadTranslation(activeLanguage);
    } else {
      setTranslationEntries({});
    }
  }, [activeLanguage]);

  const handleSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setMessage(null);
    setSaving(true);

    const languageCode = languageInput.trim();
    if (!languageCode) {
      setError(t('translations.languageCodeLabel'));
      setSaving(false);
      return;
    }

    try {
      await updateTranslationFile(languageCode, unflattenTranslationData(translationEntries));
      setMessage(t('translations.savedMessage'));
      setActiveLanguage(languageCode);
      await loadLanguages();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('translations.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleLoadClick = () => {
    const languageCode = languageInput.trim();
    if (!languageCode) {
      setError(t('translations.languageCodeLabel'));
      return;
    }

    if (languageCode === activeLanguage) {
      void loadTranslation(languageCode);
      return;
    }

    setActiveLanguage(languageCode);
  };

  const handleLanguageSelectChange = (languageCode: string) => {
    setLanguageInput(languageCode);

    if (!languageCode) {
      setActiveLanguage('');
      return;
    }

    if (languageCode === activeLanguage) {
      void loadTranslation(languageCode);
      return;
    }

    setActiveLanguage(languageCode);
  };

  const handleTranslationEntryChange = (key: string, value: string) => {
    setTranslationEntries((prev) => ({ ...prev, [key]: value }));
  };

  const handleAddTranslation = () => {
    const key = newTranslationKey.trim();
    if (!key) {
      setError(t('translations.translationKeyRequired') ?? 'Translation key is required.');
      return;
    }

    setTranslationEntries((prev) => ({ ...prev, [key]: newTranslationValue }));
    setNewTranslationKey('');
    setNewTranslationValue('');
    setError(null);
  };

  const entryKeys = Object.keys(translationEntries).sort();

  return (
    <div className="page-grid" style={{ gap: '1.5rem' }}>
      <div className="section-heading">
        <h2>{t('pages.translationsTitle')}</h2>
        <p>{t('pages.translationsDescription')}</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      <form className="form-grid" onSubmit={handleSave}>
        <div className="form-group">
          <label htmlFor="language-code">{t('translations.languageCodeLabel')}</label>
          <input
            id="language-code"
            className="form-control"
            value={languageInput}
            onChange={(e) => setLanguageInput(e.target.value.trim())}
            placeholder={t('translations.newLanguagePlaceholder')}
          />
        </div>

        <div className="form-group">
          <label htmlFor="language-select">{t('translations.availableLanguages')}</label>
          <select
            id="language-select"
            className="form-control"
            value={languageInput}
            onChange={(e) => handleLanguageSelectChange(e.target.value)}
          >
            <option value="">{t('translations.selectLanguage')}</option>
            {languages.map((language) => (
              <option key={language.languageCode} value={language.languageCode}>
                {language.languageCode} {language.updatedAt ? `(${new Date(language.updatedAt).toLocaleString()})` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>{t('translations.translationEntriesLabel') ?? 'Translation Entries'}</label>
          {entryKeys.length === 0 ? (
            <div className="alert alert-info">{t('translations.noEntriesMessage') ?? 'No translation entries found.'}</div>
          ) : (
            entryKeys.map((key) => (
              <div key={key} className="form-group">
                <label htmlFor={`translation-${key}`}>{key}</label>
                <input
                  id={`translation-${key}`}
                  className="form-control"
                  value={translationEntries[key]}
                  onChange={(e) => handleTranslationEntryChange(key, e.target.value)}
                />
              </div>
            ))
          )}
        </div>

        <div className="form-grid" style={{ gap: '0.75rem' }}>
          <div className="form-group">
            <label htmlFor="new-translation-key">{t('translations.newTranslationKeyLabel') ?? 'New translation key'}</label>
            <input
              id="new-translation-key"
              className="form-control"
              value={newTranslationKey}
              onChange={(e) => setNewTranslationKey(e.target.value)}
              placeholder={t('translations.newTranslationKeyPlaceholder') ?? 'e.g. home.title'}
            />
          </div>
          <div className="form-group">
            <label htmlFor="new-translation-value">{t('translations.newTranslationValueLabel') ?? 'New translation value'}</label>
            <input
              id="new-translation-value"
              className="form-control"
              value={newTranslationValue}
              onChange={(e) => setNewTranslationValue(e.target.value)}
              placeholder={t('translations.newTranslationValuePlaceholder') ?? 'Translation text'}
            />
          </div>
          <div style={{ alignSelf: 'flex-end' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleAddTranslation}
            >
              {t('translations.addTranslationButton') ?? 'Add entry'}
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button type="submit" className="btn btn-primary" disabled={saving || loading || !languageInput}>
            {saving ? t('common.loading') : t('common.save')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={loading || !languageInput}
            onClick={handleLoadClick}
          >
            {t('translations.loadOrCreateButton')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={loading || !activeLanguage}
            onClick={() => activeLanguage && void loadTranslation(activeLanguage)}
          >
            {t('common.refresh', 'Refresh')}
          </button>
        </div>
      </form>

      <div style={{ marginTop: '1rem', color: 'var(--color-muted)' }}>
        {languages.length > 0 ? (
          <p>{t('translations.availableLanguages')}: {languages.length}</p>
        ) : (
          <p>{t('translations.loadFailed')}</p>
        )}
      </div>
    </div>
  );
};

export default TranslationsPage;
